/**
 * Measure tool — the VisBug-style info card shown while Alt is held (or in
 * "Wrap element…" mode): selector, size and the computed styles a designer
 * checks first. Only values that differ from the browser default are
 * listed, so the card stays short.
 */

import styles from './styles/elementInfo.module.css';
import { formatDistance } from './units';
import type { UnitSettings } from './units';

/** Class for the card container — the overlay creates the element, this file fills it. */
export const infoCardClass = styles.card;

/** Hashed CSS-module class lists get long; the rest is summarised as "+N". */
const MAX_CLASSES_SHOWN = 3;
const LAYOUT_DISPLAYS = ['flex', 'inline-flex', 'grid', 'inline-grid'];

interface InfoRow {
    name: string;
    /** Already-escaped HTML (may contain a color swatch). */
    valueHtml: string;
}

function escapeHtml(text: string): string {
    return text.replace(/[&<>"]/g, (char) => `&#${char.charCodeAt(0)};`);
}

/**
 * `rgb(255, 45, 155)` → `#ff2d9b`, `rgba(0, 0, 0, 0.5)` → `#00000080`.
 * Anything that isn't rgb()/rgba() is returned unchanged.
 */
function toHex(color: string): string {
    if (!color.startsWith('rgb')) return color;
    const channels = color.match(/[\d.]+/g)?.map(Number);
    if (!channels || channels.length < 3) return color;

    const [red, green, blue, alpha = 1] = channels;
    const toTwoDigits = (value: number): string => Math.round(value).toString(16).padStart(2, '0');
    const alphaSuffix = alpha < 1 ? toTwoDigits(alpha * 255) : '';
    return `#${toTwoDigits(red)}${toTwoDigits(green)}${toTwoDigits(blue)}${alphaSuffix}`;
}

function isTransparent(color: string): boolean {
    return color === 'transparent' || /rgba\(.*,\s*0\)$/.test(color);
}

/** `div#id.class-a.class-b +2` */
export function selectorOf(element: Element): string {
    const tag = element.tagName.toLowerCase();
    const id = element.id ? `#${element.id}` : '';
    const classes = Array.from(element.classList);
    const shownClasses = classes
        .slice(0, MAX_CLASSES_SHOWN)
        .map((className) => `.${className}`)
        .join('');
    const hiddenCount = classes.length - MAX_CLASSES_SHOWN;
    const more = hiddenCount > 0 ? ` +${hiddenCount}` : '';
    return tag + id + shownClasses + more;
}

/**
 * Four sides collapsed like CSS shorthand, or null when all are zero:
 * `8px 8px 8px 8px` → `8px`, `8px 16px 8px 16px` → `8px 16px`.
 */
function shorthandSides(top: string, right: string, bottom: string, left: string): string | null {
    const allZero = [top, right, bottom, left].every((value) => parseFloat(value) === 0);
    if (allZero) return null;
    if (top === right && right === bottom && bottom === left) return top;
    if (top === bottom && right === left) return `${top} ${right}`;
    return `${top} ${right} ${bottom} ${left}`;
}

/** A small square in the color, followed by its hex value. */
function colorHtml(color: string): string {
    const swatch = `<i class="${styles.swatch}" style="background:${escapeHtml(color)}"></i>`;
    return swatch + escapeHtml(toHex(color));
}

function fontRows(style: CSSStyleDeclaration): InfoRow[] {
    const family = style.fontFamily.split(',')[0].replace(/["']/g, '').trim();
    const font = `${style.fontSize} / ${style.lineHeight} · ${style.fontWeight} · ${family}`;
    return [
        { name: 'font', valueHtml: escapeHtml(font) },
        { name: 'color', valueHtml: colorHtml(style.color) },
    ];
}

function backgroundRows(style: CSSStyleDeclaration): InfoRow[] {
    const rows: InfoRow[] = [];
    if (!isTransparent(style.backgroundColor)) {
        rows.push({ name: 'background', valueHtml: colorHtml(style.backgroundColor) });
    }
    if (style.backgroundImage !== 'none') {
        const url = /url\(["']?([^"')]+)/.exec(style.backgroundImage)?.[1];
        const fileName = url ? (url.split('/').pop() ?? url) : 'gradient';
        rows.push({ name: 'bg image', valueHtml: escapeHtml(fileName) });
    }
    return rows;
}

function boxModelRows(style: CSSStyleDeclaration): InfoRow[] {
    const rows: InfoRow[] = [];
    const padding = shorthandSides(style.paddingTop, style.paddingRight, style.paddingBottom, style.paddingLeft);
    const margin = shorthandSides(style.marginTop, style.marginRight, style.marginBottom, style.marginLeft);
    const borderWidth = shorthandSides(
        style.borderTopWidth,
        style.borderRightWidth,
        style.borderBottomWidth,
        style.borderLeftWidth,
    );
    if (padding) rows.push({ name: 'padding', valueHtml: escapeHtml(padding) });
    if (margin) rows.push({ name: 'margin', valueHtml: escapeHtml(margin) });
    if (parseFloat(style.borderTopLeftRadius) > 0) {
        rows.push({ name: 'radius', valueHtml: escapeHtml(style.borderRadius) });
    }
    if (borderWidth) {
        const border = `${escapeHtml(borderWidth)} ${escapeHtml(style.borderTopStyle)} ${colorHtml(style.borderTopColor)}`;
        rows.push({ name: 'border', valueHtml: border });
    }
    return rows;
}

function layoutRows(style: CSSStyleDeclaration): InfoRow[] {
    if (!LAYOUT_DISPLAYS.includes(style.display)) return [];
    const isFlex = style.display.endsWith('flex');
    const direction = isFlex && style.flexDirection !== 'row' ? ` ${style.flexDirection}` : '';
    const hasGap = style.gap !== 'normal' && parseFloat(style.gap) > 0;
    const gap = hasGap ? ` · gap ${style.gap}` : '';
    return [{ name: 'layout', valueHtml: escapeHtml(style.display + direction + gap) }];
}

function extraRows(element: Element, style: CSSStyleDeclaration): InfoRow[] {
    const rows: InfoRow[] = [];
    if (parseFloat(style.opacity) < 1) rows.push({ name: 'opacity', valueHtml: escapeHtml(style.opacity) });
    const testId = element.getAttribute('data-testid');
    if (testId) rows.push({ name: 'test id', valueHtml: escapeHtml(testId) });
    return rows;
}

/** Inner HTML for the info card describing `element`; the size uses the tool's current unit. */
export function describeElement(element: Element, units: UnitSettings): string {
    const style = getComputedStyle(element);
    const { width, height } = element.getBoundingClientRect();
    const hasText = !!element.textContent?.trim();

    const rows = [
        ...(hasText ? fontRows(style) : []),
        ...backgroundRows(style),
        ...boxModelRows(style),
        ...layoutRows(style),
        ...extraRows(element, style),
    ];

    const size = `${formatDistance(width, units, 'x')} × ${formatDistance(height, units, 'y')}`;
    const rowsHtml = rows
        .map(
            (row) =>
                `<div class="${styles.row}"><span class="${styles.name}">${row.name}</span><span>${row.valueHtml}</span></div>`,
        )
        .join('');

    return (
        `<div class="${styles.selector}">${escapeHtml(selectorOf(element))}</div>` +
        `<div class="${styles.size}">${escapeHtml(size)}</div>` +
        rowsHtml
    );
}
